import { useState, useEffect, useRef, useMemo } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const faqs = [
  {
    question: 'What qualifies a lead?',
    answer:
      'A lead is a land parcel that meets our minimum investment score threshold (70+) combined with at least one trigger: tax delinquency, recent inheritance transfer, out-of-state owner, or subdivision activity nearby. Each lead is verified through county records and scored on growth potential, development activity, utility access, and flood risk before it ever reaches your dashboard.',
    tags: ['data', 'features'],
  },
  {
    question: 'Is the parcel data exclusive?',
    answer:
      'We provide parcel intelligence data sourced from public records — the same data is theoretically available to anyone who knows where to look. What makes Parcel Lead Pro valuable is the scoring, filtering, and aggregation: we analyze 8 development factors, run the math, and surface the best opportunities so you do not have to dig through county databases manually.',
    tags: ['data', 'overview'],
  },
  {
    question: 'What states do you cover?',
    answer:
      'We currently have parcel data for 3 counties in South Carolina: Dorchester, York, and Lancaster, with approximately 5,000 live parcel records. Additional states are planned for expansion (TX, VA, FL, GA, TN, NC). Contact us to check specific county availability in your target market.',
    tags: ['coverage', 'data'],
  },
  {
    question: 'How many counties can I target?',
    answer:
      'All plans can search any county in our live coverage area (Dorchester, York, Lancaster SC). Limits are on monthly searches, not counties: Starter gets 25/mo, Investor gets 200/mo, Pro gets 500/mo, and Enterprise is unlimited. Starter, Investor, and Pro all include a 1-week free trial. Mecklenburg NC will be added once real authoritative ingestion is complete.',
    tags: ['pricing', 'coverage'],
  },
  {
    question: 'How does the replacement policy work?',
    answer:
      'Since our platform provides parcel intelligence data rather than traditional leads, we do not offer lead replacements. All data is sourced directly from public county records. If you find inaccurate data, please submit a ticket through our Support Center and we will investigate the source.',
    tags: ['billing', 'data'],
  },
  {
    question: 'What CRM integrations are available?',
    answer:
      'All plans include CSV export — simply download and import into any CRM. Direct integrations with Salesforce, HubSpot, and Pipedrive are planned for Enterprise plans. API access is also planned for Enterprise customers who want to build custom integrations.',
    tags: ['integrations', 'features'],
  },
  {
    question: 'What is Parcel Lead Pro and how does it work?',
    answer:
      'Parcel Lead Pro is a parcel intelligence platform designed to aggregate and score land parcel data across the United States. Our platform allows you to search, filter, and score parcels based on criteria including zoning, acreage, ownership history, tax delinquency, and more. You can visualize opportunities on interactive heat maps and export lead lists. CRM integrations are planned for Enterprise plans.',
    tags: ['overview', 'getting started'],
  },
  {
    question: 'Where does your parcel data come from?',
    answer:
      'Data is sourced from free government sources: county ArcGIS REST services, state open data portals, FEMA flood zone APIs, and USGS elevation data. We currently have coverage for South Carolina (Dorchester, York, Lancaster counties), with plans to expand to additional states over time.',
    tags: ['data', 'sources'],
  },
  {
    question: 'Is there a free trial available?',
    answer:
      'Start with Starter — $39/mo with a 1-week free trial.  You get 25 parcel searches per month, Deal Scores, and access to all covered counties. Upgrade to Investor ($79/mo) or Pro ($129/mo) anytime for more searches and advanced features.',
    tags: ['pricing', 'trial'],
  },
  {
    question: 'What is the scoring engine and how are scores calculated?',
    answer:
      'Our Deal Score algorithm evaluates each parcel on a 0-100 scale across eight weighted factors: Population Investor (25%), Commercial Investor (20%), Utility Access (15%), Highway Proximity (10%), New Subdivisions (10%), Land Appreciation (10%), School Construction (5%), and Flood Risk (5%). Investor and Pro plans unlock the full factor-by-factor breakdown.',
    tags: ['features', 'scoring'],
  },
  {
    question: 'Can I export data and integrate with my existing tools?',
    answer:
      'All plans include CSV export — download and import into any CRM or spreadsheet. Investor and Pro plans include PDF property reports. Direct CRM integrations and API access are planned for Enterprise plans.',
    tags: ['integrations', 'export'],
  },
  {
    question: 'What happens if I exceed my parcel lookup limit?',
    answer:
      'Starter users get 25 parcel searches per month — once reached, you will see a notice to upgrade. Investor allows 200/mo, Pro allows 500/mo, and Enterprise is unlimited. Starter, Investor, and Pro all include a 1-week free trial. You can upgrade at any time from your account dashboard.',
    tags: ['billing', 'limits'],
  },
  {
    question: 'How do I cancel or change my plan?',
    answer:
      'You can upgrade, downgrade, or cancel your subscription at any time from your account dashboard. Changes take effect at your next billing cycle. If you cancel, you will retain access until the end of your current billing period. We do not offer refunds for partial months.',
    tags: ['billing', 'account'],
  },
  {
    question: 'Is my data secure?',
    answer:
      'Security is our top priority. All data is encrypted in transit (TLS 1.3) and at rest (AES-256). We undergo regular security reviews and use industry-standard practices to protect your data. We never sell or share your data with third parties.',
    tags: ['security', 'privacy'],
  },
  {
    question: 'Do you have development site selection data?',
    answer:
      'Yes! Our Investment Score evaluates parcels for residential, commercial, and industrial development potential. We analyze acreage, zoning (R-1, C-1, M-1/M-2, I-1), utility access (water, sewer, power), highway proximity, flood risk (X-zone preferred), and growth indicators. Each parcel receives a 0-100 investment score with Strong Buy (85+), Buy (70-84), and Hold (<70) ratings.',
    tags: ['features', 'scoring'],
  },
  {
    question: 'How often is the data updated?',
    answer:
      'Parcel data refreshes weekly via automated ingestion from county ArcGIS REST endpoints. Our system runs a full sync every 3 days for active counties. Tax delinquency data updates monthly as counties publish new rolls. You can see the last refresh date for each county in your dashboard.',
    tags: ['coverage', 'data'],
  },
];

const quickFilters = ['All', 'Pricing', 'Features', 'Data', 'Billing', 'Security'];

export default function FAQ() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');

  const filteredFaqs = useMemo(() => {
    let results = faqs;
    if (activeFilter !== 'All') {
      results = results.filter((f) => f.tags.some((t) => t.toLowerCase() === activeFilter.toLowerCase()));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      results = results.filter((f) => f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q));
    }
    return results;
  }, [search, activeFilter]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const items = section.querySelectorAll('.faq-item');
    const tl = gsap.timeline({ scrollTrigger: { trigger: section, start: 'top 75%', once: true } });
    tl.fromTo(items, { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, stagger: 0.08, ease: 'power3.out' });
    return () => { tl.kill(); };
  }, [filteredFaqs]);

  return (
    <section ref={sectionRef} id="faq" className="py-20 md:py-28" style={{ background: '#07110D' }}>
      <div className="mx-auto max-w-[800px] px-6">
        <span className="label-accent mb-4 block text-center" style={{ color: '#7CFF6B' }}>FAQ</span>
        <h2 className="font-display mb-6 text-center font-semibold" style={{ fontSize: 'clamp(36px, 4vw, 48px)', lineHeight: 1.1, color: '#F7F8F4' }}>
          Questions? Answered.
        </h2>

        {/* Search */}
        <div className="faq-item relative mb-6 opacity-0">
          <svg className="absolute left-4 top-1/2 -translate-y-1/2" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <circle cx="7.5" cy="7.5" r="5.5" stroke="#A8B8AE" strokeWidth="1.5" />
            <path d="M11.5 11.5L16 16" stroke="#A8B8AE" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search questions..."
            className="font-body w-full rounded-xl border border-white/10 py-3.5 pl-11 pr-4 text-sm outline-none transition-all focus:ring-2 focus:ring-[#7CFF6B]"
            style={{ background: '#0B1620', color: '#F7F8F4' }}
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-xs" style={{ color: '#a9b4c0' }}>
              Clear
            </button>
          )}
        </div>

        {/* Quick filters */}
        <div className="faq-item mb-6 flex flex-wrap gap-2 opacity-0">
          {quickFilters.map((f) => (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              className="rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-200"
              style={{
                background: activeFilter === f ? '#7CFF6B' : '#0B1620',
                color: activeFilter === f ? '#07110D' : '#a9b4c0',
                border: activeFilter === f ? 'none' : '1px solid rgba(255,255,255,0.1)',
              }}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Results count */}
        {(search || activeFilter !== 'All') && (
          <p className="faq-item mb-4 text-center text-sm" style={{ color: '#a9b4c0' }}>
            {filteredFaqs.length} result{filteredFaqs.length !== 1 ? 's' : ''}
            {search && ` for "${search}"`}
            {activeFilter !== 'All' && ` in ${activeFilter}`}
          </p>
        )}

        {/* FAQ items */}
        <div className="flex flex-col gap-3">
          {filteredFaqs.length === 0 ? (
            <div className="faq-item rounded-xl py-12 text-center opacity-0 border border-white/10" style={{ background: '#0B1620' }}>
              <svg className="mx-auto mb-3" width="32" height="32" viewBox="0 0 32 32" fill="none">
                <circle cx="14" cy="14" r="9" stroke="#A8B8AE" strokeWidth="2" />
                <path d="M20 20L27 27" stroke="#A8B8AE" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <p className="font-body" style={{ color: '#a9b4c0' }}>No questions match your search.</p>
              <button onClick={() => { setSearch(''); setActiveFilter('All'); }} className="mt-2 text-sm underline" style={{ color: '#7CFF6B' }}>Clear filters</button>
            </div>
          ) : (
            filteredFaqs.map((faq) => {
              const originalIndex = faqs.indexOf(faq);
              return (
                <div
                  key={originalIndex}
                  className="faq-item overflow-hidden opacity-0 border border-white/10"
                  style={{ background: '#0B1620', borderRadius: 10 }}
                >
                  <button
                    className="flex w-full items-center justify-between px-6 py-5 text-left transition-colors duration-200 hover:bg-white/[0.02]"
                    onClick={() => setOpenIndex(openIndex === originalIndex ? null : originalIndex)}
                  >
                    <span className="font-body pr-4 font-medium" style={{ fontSize: 16, color: '#F7F8F4', lineHeight: 1.4 }}>
                      {faq.question}
                    </span>
                    <span
                      className="flex-shrink-0 transition-transform duration-300"
                      style={{ transform: openIndex === originalIndex ? 'rotate(45deg)' : 'rotate(0deg)', color: '#7CFF6B' }}
                    >
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </span>
                  </button>
                  <div
                    className="overflow-hidden transition-all duration-300"
                    style={{ maxHeight: openIndex === originalIndex ? 500 : 0, opacity: openIndex === originalIndex ? 1 : 0 }}
                  >
                    <p className="font-body px-6 pb-5" style={{ fontSize: 15, lineHeight: 1.6, color: '#a9b4c0' }}>
                      {faq.answer}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
}
